// The issued Diploma, and the claim flow that issues it -- see ADR-0009.
//
// Print styles are the deliverable rather than a generated PDF: browsers print
// to PDF perfectly well, and a print stylesheet stays in step with the page
// instead of being a second rendering that can drift from it.

import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, SignInButton } from '@clerk/react';
import theme from '../../theme';
import PageHeader from '../../components/PageHeader';
import { LEARN_SUBDOMAIN_URL } from '../../lib/subdomain';
import { fetchMyCertificate, issueCertificate } from '../../lib/capstoneApi';

const CSS = `
  /* A landscape sheet with a double rule, corner flourishes and a seal --
     the traditional register, chosen deliberately (see the design note in the
     Certificate component below). */
  .cert-sheet {
    position: relative;
    background: #fffdf8;
    color: #2a2420;
    width: 100%;
    max-width: 1000px;
    aspect-ratio: 1.414 / 1;
    margin: 8px auto 28px;
    padding: 44px 58px 30px;
    box-sizing: border-box;
    text-align: center;
    border: 2px solid rgba(125, 95, 46, 0.75);
    display: flex;
    flex-direction: column;
  }
  /* The sheet is deliberately light in both themes: it is a document, and a
     dark-mode certificate would print as a black rectangle. */
  .cert-sheet::before {
    content: '';
    position: absolute;
    inset: 11px;
    border: 1px solid rgba(125, 95, 46, 0.4);
    pointer-events: none;
  }
  .cert-corner { position: absolute; width: 44px; height: 44px; color: rgba(125, 95, 46, 0.75); }
  .cert-corner.tl { top: 18px; left: 18px; }
  .cert-corner.tr { top: 18px; right: 18px; transform: scaleX(-1); }
  .cert-corner.bl { bottom: 18px; left: 18px; transform: scaleY(-1); }
  .cert-corner.br { bottom: 18px; right: 18px; transform: scale(-1, -1); }

  .cert-inst {
    font-family: 'Josefin Sans', sans-serif;
    font-size: 13px;
    letter-spacing: 0.42em;
    text-transform: uppercase;
    color: #7d5f2e;
    margin: 4px 0 0;
  }
  .cert-rule { width: 150px; height: 1px; background: rgba(125, 95, 46, 0.5); margin: 12px auto 0; }
  .cert-main { flex: 1; display: flex; flex-direction: column; justify-content: center; }
  .cert-confers { font-family: 'EB Garamond', serif; font-style: italic; font-size: 16px; color: #6b6558; margin: 0 0 6px; }
  .cert-name {
    font-family: 'Cormorant Garamond', serif;
    font-weight: 500;
    font-size: 58px;
    line-height: 1.05;
    margin: 0;
    letter-spacing: 0.01em;
    text-wrap: balance;
  }
  .cert-namerule { width: 360px; max-width: 70%; height: 1px; background: rgba(125, 95, 46, 0.35); margin: 14px auto 16px; }
  .cert-having { font-family: 'EB Garamond', serif; font-size: 15.5px; color: #6b6558; margin: 0 0 8px; }
  .cert-cred { font-family: 'Cormorant Garamond', serif; font-size: 32px; color: #7d5f2e; margin: 0 0 16px; letter-spacing: 0.01em; }
  .cert-body { font-family: 'EB Garamond', serif; font-size: 15px; line-height: 1.7; color: #6b6558; max-width: 60ch; margin: 0 auto; }
  .cert-date { font-family: 'EB Garamond', serif; font-size: 15px; color: #6b6558; font-style: italic; margin: 20px 0 0; }

  .cert-foot { display: flex; align-items: flex-end; justify-content: space-between; gap: 24px; }
  .cert-sig { width: 264px; }
  .cert-sig .line { height: 1px; background: rgba(125, 95, 46, 0.55); margin-bottom: 7px; }
  .cert-sig .who { font-family: 'Cormorant Garamond', serif; font-size: 19px; color: #2a2420; }
  .cert-sig .role {
    font-family: 'Josefin Sans', sans-serif;
    font-size: 9.5px;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: #6b6558;
    margin-top: 3px;
  }
  .cert-seal { flex-shrink: 0; }
  .cert-verify-note {
    font-family: 'Josefin Sans', sans-serif;
    font-size: 9.5px;
    letter-spacing: 0.1em;
    color: #6b6558;
    margin: 12px 0 0;
  }

  /* Below roughly a tablet the landscape sheet stops being legible, so it
     relaxes into a portrait card rather than shrinking the type to nothing. */
  @media (max-width: 720px) {
    .cert-sheet { aspect-ratio: auto; padding: 32px 24px 24px; }
    .cert-name { font-size: 38px; }
    .cert-cred { font-size: 24px; }
    .cert-foot { flex-direction: column; align-items: center; gap: 26px; }
    .cert-sig { width: 100%; max-width: 280px; text-align: center !important; }
    .cert-corner { display: none; }
  }

  .cert-actions { display: flex; flex-wrap: wrap; gap: 14px; justify-content: center; margin-bottom: 30px; }
  .cert-locked {
    max-width: 620px;
    margin: 0 auto;
    padding: 26px 30px;
    background: ${theme.bgCard};
    border: 1px solid rgba(var(--ap-text-muted-rgb), 0.25);
    border-radius: 4px;
  }
  .cert-gates { list-style: none; margin: 18px 0 0; padding: 0; }
  .cert-gates li {
    display: flex;
    gap: 10px;
    align-items: baseline;
    font-family: 'EB Garamond', serif;
    font-size: 16px;
    color: ${theme.textMuted};
    padding: 7px 0;
  }
  .cert-gates .mark { font-family: 'Josefin Sans', sans-serif; font-size: 13px; }
  .cert-gates .yes { color: var(--ap-success); }
  .cert-gates .no { color: ${theme.textMuted}; }
  .cert-err { color: var(--ap-error); font-size: 14px; text-align: center; }

  @page { size: A4 landscape; margin: 12mm; }
  @media print {
    /* Only the certificate itself should reach the paper. */
    body * { visibility: hidden; }
    .cert-sheet, .cert-sheet * { visibility: visible; }
    .cert-sheet {
      position: absolute;
      left: 0; top: 0;
      width: 100%; max-width: none;
      margin: 0;
      border-color: #7d5f2e;
      box-shadow: none;
      /* The colours are already literals rather than theme tokens, so the
         sheet prints identically whichever theme the screen was in. */
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    /* The landscape proportions are the whole point on paper, so the mobile
       relaxation must not leak into print on a narrow window. */
    .cert-sheet { aspect-ratio: 1.414 / 1; padding: 44px 58px 30px; }
    .cert-corner { display: block; }
    .cert-foot { flex-direction: row; align-items: flex-end; }
    .cert-sig { width: 264px; }
  }
`;

const ONES = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten',
  'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
const ORDINALS = ['', 'first', 'second', 'third', 'fourth', 'fifth', 'sixth', 'seventh', 'eighth',
  'ninth', 'tenth', 'eleventh', 'twelfth', 'thirteenth', 'fourteenth', 'fifteenth', 'sixteenth',
  'seventeenth', 'eighteenth', 'nineteenth', 'twentieth'];

// "sixth", "twenty-first", "thirty-first" -- only ever 1-31, so this does not
// need to be a general-purpose number speller.
export function ordinalWords(day) {
  if (day <= 20) return ORDINALS[day];
  if (day === 30) return 'thirtieth';
  const prefix = day < 30 ? 'twenty' : 'thirty';
  return `${prefix}-${ORDINALS[day % 10]}`;
}

// "two thousand twenty-six" -- likewise only needs to span plausible years.
function yearWords(year) {
  const thousands = Math.floor(year / 1000);
  const remainder = year % 1000;
  const hundreds = Math.floor(remainder / 100);
  const rest = remainder % 100;
  const parts = [`${ONES[thousands]} thousand`];
  if (hundreds) parts.push(`${ONES[hundreds]} hundred`);
  if (rest) {
    if (rest < 20) parts.push(ONES[rest]);
    else {
      const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];
      const unit = rest % 10;
      parts.push(unit ? `${tens[Math.floor(rest / 10)]}-${ONES[unit]}` : tens[Math.floor(rest / 10)]);
    }
  }
  return parts.join(' ');
}

// The date is spelled out because that is the convention the form follows;
// the machine-readable date is on the verification page.
function formalDate(value) {
  if (!value) return '';
  const d = new Date(value);
  const month = d.toLocaleDateString('en-GB', { month: 'long' });
  return `Awarded this ${ordinalWords(d.getDate())} day of ${month}, ${yearWords(d.getFullYear())}`;
}

function CornerFlourish({ position }) {
  return (
    <svg className={`cert-corner ${position}`} viewBox="0 0 44 44" fill="none" stroke="currentColor" strokeWidth="1.1" aria-hidden="true">
      <path d="M0 14 C0 6 6 0 14 0" />
      <path d="M0 22 C0 10 10 0 22 0" opacity=".55" />
      <circle cx="7.5" cy="7.5" r="1.8" fill="currentColor" stroke="none" />
    </svg>
  );
}

// Geometric rather than heraldic: a monogram and the institution's own name,
// with no crest, motto or anything implying an accrediting body that does not
// exist. The two arcs are separate paths so the lower text reads upright
// rather than upside down.
function Seal({ year }) {
  return (
    <svg className="cert-seal" width="122" height="122" viewBox="0 0 118 118" aria-hidden="true">
      <defs>
        <path id="cert-ring-top" d="M 16,59 A 43,43 0 0,1 102,59" fill="none" />
        <path id="cert-ring-bot" d="M 18.5,59 A 40.5,40.5 0 0,0 99.5,59" fill="none" />
      </defs>
      <circle cx="59" cy="59" r="55" fill="none" stroke="rgba(125,95,46,.7)" strokeWidth="1.4" />
      <circle cx="59" cy="59" r="50.5" fill="none" stroke="rgba(125,95,46,.35)" strokeWidth="1" />
      <circle cx="59" cy="59" r="33" fill="none" stroke="rgba(125,95,46,.45)" strokeWidth="1" />
      <g stroke="rgba(125,95,46,.4)" strokeWidth="1">
        <line x1="59" y1="6" x2="59" y2="12" /><line x1="59" y1="106" x2="59" y2="112" />
        <line x1="6" y1="59" x2="12" y2="59" /><line x1="106" y1="59" x2="112" y2="59" />
      </g>
      <text fontFamily="'Josefin Sans', sans-serif" fontSize="8.6" letterSpacing="2.8" fill="rgba(125,95,46,.9)">
        <textPath href="#cert-ring-top" startOffset="50%" textAnchor="middle">AUSTRIAN PROCESS</textPath>
      </text>
      <text fontFamily="'Josefin Sans', sans-serif" fontSize="7.6" letterSpacing="2.4" fill="rgba(125,95,46,.7)">
        <textPath href="#cert-ring-bot" startOffset="50%" textAnchor="middle">{year}</textPath>
      </text>
      <text x="59" y="67" textAnchor="middle" fontFamily="'Cormorant Garamond', serif" fontWeight="600" fontSize="30" fill="rgba(125,95,46,.95)">AP</text>
    </svg>
  );
}

const ROMAN = [[1000, 'M'], [900, 'CM'], [500, 'D'], [400, 'CD'], [100, 'C'], [90, 'XC'],
  [50, 'L'], [40, 'XL'], [10, 'X'], [9, 'IX'], [5, 'V'], [4, 'IV'], [1, 'I']];
function toRoman(n) {
  return ROMAN.reduce((acc, [v, s]) => { while (n >= v) { acc += s; n -= v; } return acc; }, '');
}

function Certificate({ cert }) {
  const verifyUrl = `${LEARN_SUBDOMAIN_URL}/verify/${cert.verificationCode}`;
  return (
    <>
      <div className="cert-sheet">
        <CornerFlourish position="tl" />
        <CornerFlourish position="tr" />
        <CornerFlourish position="bl" />
        <CornerFlourish position="br" />

        <p className="cert-inst">Austrian Process</p>
        <div className="cert-rule" />

        <div className="cert-main">
          <p className="cert-confers">This is to certify that</p>
          <h2 className="cert-name">{cert.recipientName}</h2>
          <div className="cert-namerule" />
          <p className="cert-having">having satisfied the requirements of the programme, is awarded the</p>
          <p className="cert-cred">{cert.credential}</p>
          <p className="cert-body">
            in recognition of the completion of the three-level curriculum, the defence of an
            applied research essay under human review, and the passing of the comprehensive
            examination across all three levels.
          </p>
          <p className="cert-date">{formalDate(cert.issuedAt)}</p>
        </div>

        <div className="cert-foot">
          <div className="cert-sig">
            <div className="line" />
            {/* Names the reviewer who actually read the essay. Absent, the
                line stays blank rather than inventing an authority. */}
            <div className="who">{cert.signedByName || ' '}</div>
            <div className="role">
              {cert.signedByName ? 'Reviewer · Austrian Process' : 'Reviewer'}
            </div>
          </div>

          <Seal year={toRoman(new Date(cert.issuedAt).getFullYear())} />

          <div className="cert-sig" style={{ textAlign: 'right' }}>
            <div className="line" />
            <div className="who">{cert.verificationCode}</div>
            <div className="role">Verification code</div>
          </div>
        </div>

        <p className="cert-verify-note">Verify this certificate at {verifyUrl.replace('https://', '')}</p>
      </div>

      <div className="cert-actions">
        <button type="button" className="btn-primary" onClick={() => window.print()}>
          Print / Save as PDF
        </button>
        <Link to={`/verify/${cert.verificationCode}`} className="btn-secondary" style={{ textDecoration: 'none' }}>
          View public verification
        </Link>
      </div>
    </>
  );
}

function CertificatePage() {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const [state, setState] = useState(null);
  const [error, setError] = useState('');
  const [claiming, setClaiming] = useState(false);

  const load = useCallback(async () => {
    try {
      setState(await fetchMyCertificate(getToken));
    } catch (err) {
      setError(err.message);
    }
  }, [getToken]);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Certificate - Austrian Process';
    if (isLoaded && isSignedIn) load();
  }, [isLoaded, isSignedIn, load]);

  async function claim() {
    setClaiming(true);
    setError('');
    try {
      const res = await issueCertificate(getToken);
      setState((prev) => ({ ...prev, certificate: res.certificate }));
    } catch (err) {
      setError(err.message);
    } finally {
      setClaiming(false);
    }
  }

  return (
    <div className="page-container">
      <style>{CSS}</style>
      <PageHeader
        title="Certificate"
        description="The Diploma, once both gates are passed."
      />

      {!isLoaded && <p style={{ color: theme.textMuted }}>Loading...</p>}

      {isLoaded && !isSignedIn && (
        <SignInButton mode="modal">
          <button type="button" className="btn-primary">Sign in</button>
        </SignInButton>
      )}

      {isLoaded && isSignedIn && state && (
        state.certificate ? (
          <Certificate cert={state.certificate} />
        ) : (
          <div className="cert-locked">
            <p style={{ color: theme.textPrimary, margin: 0, fontFamily: "'EB Garamond', serif", fontSize: '16.5px' }}>
              {state.eligible
                ? 'Both gates are passed. Claim your Diploma below - the certificate records the date and carries a code anyone can verify.'
                : 'The Diploma is awarded once both gates are passed:'}
            </p>
            {!state.eligible && (
              <ul className="cert-gates">
                <li><span className="mark no">○</span> Applied research essay, passed under human review</li>
                <li><span className="mark no">○</span> Comprehensive exam across all three levels, at 75%</li>
              </ul>
            )}
            <div style={{ marginTop: '20px' }}>
              {state.eligible ? (
                <button type="button" className="btn-primary" onClick={claim} disabled={claiming}>
                  {claiming ? 'Issuing...' : 'Claim my Diploma'}
                </button>
              ) : (
                <Link to="/capstone" className="btn-secondary" style={{ textDecoration: 'none' }}>
                  Go to the Capstone
                </Link>
              )}
            </div>
          </div>
        )
      )}

      {error && <p className="cert-err" style={{ marginTop: '18px' }}>{error}</p>}
    </div>
  );
}

export default CertificatePage;
